import type { Options } from 'pusher-js';
import Pusher from 'pusher-js';

import { logError } from '~/common/utils/log';
import { buildPusherClientOptions } from '~/config/pusherClientOptions';
import { registerGamificationAuthHandler } from '~/domains/promotions/domains/gamification/config/gamificationChannelAuth';
import { registerLiveActivityAuthHandler } from '~/services/live-activity/config/liveActivityChannelAuth';

// Type definition for channel authorization data returned by Pusher
// This base type can be extended by service-specific auth responses
// (e.g., PusherUserAuthSubscriptionsResponse from live-activity service)
interface ChannelAuthorizationData {
  auth: string;
  channel_data?: string;
  shared_secret?: string;
}

// Union type for all possible auth response types
// Can be extended with additional service-specific types in the future
type PusherAuthInfo = ChannelAuthorizationData | null;

// Type definition for channel authorization handlers
type ChannelAuthHandler = (
  socketId: string,
  channelName: string,
  callback: (error: Error | null, authInfo?: PusherAuthInfo) => void
) => void;

// Registry of auth handlers for different channel patterns
const authHandlerRegistry: Map<RegExp, ChannelAuthHandler> = new Map();

/**
 * Register an authentication handler for channels matching a specific pattern
 * @param pattern - RegExp to match channel names
 * @param handler - Function to handle authentication for matched channels
 */
export const registerChannelAuthHandler = (pattern: RegExp, handler: ChannelAuthHandler) => {
  authHandlerRegistry.set(pattern, handler);
};

/**
 * Find and execute the appropriate auth handler for a channel
 */
const routeChannelAuth = (
  socketId: string,
  channelName: string,
  callback: (error: Error | null, authInfo?: PusherAuthInfo) => void
) => {
  // Find matching auth handler
  for (const [pattern, handler] of authHandlerRegistry.entries()) {
    if (pattern.test(channelName)) {
      return handler(socketId, channelName, callback);
    }
  }

  // No handler found
  logError('No auth handler registered for channel:', { channelName });
  callback(new Error(`No auth handler registered for channel: ${channelName}`));
};

// This authorizer is ONLY called by Pusher for private-/presence- channels
// Public channels bypass this entirely (handled by Pusher SDK automatically)
const authorizer: Options['authorizer'] = (channel) => ({
  authorize: (
    socketId: string,
    callback: (error: Error | null, authInfo?: PusherAuthInfo) => void
  ) => {
    // Route to appropriate service-specific auth handler
    routeChannelAuth(socketId, channel.name, callback);
  },
});

const pusherClient = new Pusher(
  process.env.NEXT_PUBLIC_PUSHER_KEY as string,
  buildPusherClientOptions(
    {
      key: process.env.NEXT_PUBLIC_PUSHER_KEY,
      cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
      host: process.env.NEXT_PUBLIC_PUSHER_HOST,
      port: process.env.NEXT_PUBLIC_PUSHER_PORT,
    },
    authorizer
  )
);

// Register auth handlers for live-activity service channels
// This must run at module load to ensure handlers are registered before any channel subscriptions
registerLiveActivityAuthHandler();

// Register auth handler for gamification per-user channels (private-gamification-user-{sub})
registerGamificationAuthHandler();

export default pusherClient;
